// Input recording and replay: what the sim read from the action map, step by step, as plain JSON.
//
// A recording is taken from inside `Actions` while it runs (`actions.record()`): the first time a step reads an action,
// the value it got is written down against the absolute step index (the runtime clock's step). Replay
// (`actions.replay(tape)`) feeds the same values back through the same reads, so the sim takes the same path whether
// the input came from a keyboard, a pad, a test's injected holds or a tape. Steps are absolute, so a save restored at
// step 3600 picks up the tape at 3600 with no cursor to carry.
//
// Two kinds of entry, by key prefix:
//   levels  "v:<action>" a button's value (0..1), "s:<action>" a stick [x, y], "g:<key>" a game value the step
//           sampled (`Actions.sample`): stored only when they change, and carried until the next change
//   events  "p:<action>" a press edge (1), "d:<action>" the deltas consumed in that step, one [x, y] per call:
//           stored only on the steps they happen
// -0 is written as 0 (JSON can't keep it) and the live read returns the same normalised number while recording, so
// the recorded run and its replay see identical values.

/** A recorded value: a button value or press (number), a stick ([x, y]) or a step's delta reads ([[x, y], ...]). */
export type TapeValue = number | [number, number] | [number, number][];

/** One change: [absolute step, key, value]. */
export type TapeEntry = [number, string, TapeValue];

/** A recording of the sim's action reads. JSON-safe; `format` is checked on replay. */
export interface InputTape {
  format: "rpg.input-tape@1";
  /** The first step recorded. */
  from: number;
  /** One past the last step recorded. Replay covers [from, to). */
  to: number;
  /** In step order. */
  entries: TapeEntry[];
}

/** The tape format tag. */
export const INPUT_TAPE_FORMAT = "rpg.input-tape@1" as const;

/** -0 to 0. */
export const norm0 = (x: number): number => (x === 0 ? 0 : x);

const isLevel = (key: string): boolean => key.startsWith("v:") || key.startsWith("s:") || key.startsWith("g:");

function sameValue(a: TapeValue | undefined, b: TapeValue): boolean {
  if (a === undefined) return false;
  if (typeof a === "number" || typeof b === "number") return a === b;
  return a[0] === b[0] && a[1] === b[1];
}

/** Writes a tape step by step (used by Actions). */
export class TapeWriter {
  private entries: TapeEntry[] = [];
  private carry = new Map<string, TapeValue>();
  private seen = new Set<string>();
  private deltas = new Map<string, [number, number][]>();
  private from = -1;
  private step = -1;

  /** A step starts. */
  begin(step: number): void {
    this.flushDeltas();
    if (this.from < 0) this.from = step;
    this.step = step;
    this.seen.clear();
  }

  /** A level read (button value, stick). Only the first read per step is kept; it's stored if it changed. */
  level(key: string, v: TapeValue): void {
    if (this.step < 0 || this.seen.has(key)) return;
    this.seen.add(key);
    const prev = this.carry.get(key) ?? (key.startsWith("s:") ? [0, 0] : 0);
    if (sameValue(prev, v)) return;
    this.carry.set(key, v);
    this.entries.push([this.step, key, v]);
  }

  /** A press read: stored once per step, only when pressed. */
  press(key: string, on: boolean): void {
    if (this.step < 0 || this.seen.has(key)) return;
    this.seen.add(key);
    if (on) this.entries.push([this.step, key, 1]);
  }

  /** A delta consumed: every call in the step is kept, in order. */
  delta(key: string, x: number, y: number): void {
    if (this.step < 0) return;
    let list = this.deltas.get(key);
    if (!list) this.deltas.set(key, (list = []));
    list.push([x, y]);
  }

  /** The tape so far (the current step counts as recorded). */
  finish(): InputTape {
    this.flushDeltas();
    const from = Math.max(0, this.from);
    return { format: INPUT_TAPE_FORMAT, from, to: this.step < 0 ? from : this.step + 1, entries: this.entries.slice() };
  }

  private flushDeltas(): void {
    for (const [key, list] of this.deltas) if (list.some(([x, y]) => x !== 0 || y !== 0)) this.entries.push([this.step, key, list]);
    this.deltas.clear();
  }
}

/** Reads a tape back at any step (used by Actions). Stepping forward one step at a time is O(changes); a jump rebuilds. */
export class TapeReader {
  readonly tape: InputTape;
  private carry = new Map<string, TapeValue>();
  private events = new Map<string, TapeValue>();
  private calls = new Map<string, number>();
  private at = -1;
  /** Index of the first entry after the current step. */
  private next = 0;

  constructor(tape: InputTape) {
    if (!tape || tape.format !== INPUT_TAPE_FORMAT) throw new Error(`not an input tape (format ${String((tape as { format?: unknown })?.format)})`);
    this.tape = tape;
  }

  /** Whether `step` is inside the tape. */
  covers(step: number): boolean {
    return step >= this.tape.from && step < this.tape.to;
  }

  /** Move to `step` (the step about to run). */
  seek(step: number): void {
    if (step !== this.at + 1 || this.at < 0) {
      this.carry.clear();
      this.next = 0;
      const e = this.tape.entries;
      while (this.next < e.length && e[this.next]![0] < step) {
        const [, key, v] = e[this.next]!;
        if (isLevel(key)) this.carry.set(key, v);
        this.next++;
      }
    }
    this.at = step;
    this.events.clear();
    this.calls.clear();
    const e = this.tape.entries;
    while (this.next < e.length && e[this.next]![0] === step) {
      const [, key, v] = e[this.next]!;
      if (isLevel(key)) this.carry.set(key, v);
      else this.events.set(key, v);
      this.next++;
    }
  }

  /** A button's value this step. */
  value(action: string): number {
    const v = this.carry.get(`v:${action}`);
    return typeof v === "number" ? v : 0;
  }

  /** A game value sampled this step (`Actions.sample`). */
  sampled(key: string): number {
    const v = this.carry.get(`g:${key}`);
    return typeof v === "number" ? v : 0;
  }

  /** A stick this step. */
  stick(action: string): { x: number; y: number } {
    const v = this.carry.get(`s:${action}`) as [number, number] | undefined;
    return v ? { x: v[0], y: v[1] } : { x: 0, y: 0 };
  }

  /** Whether the action was pressed this step. */
  pressed(action: string): boolean {
    return this.events.has(`p:${action}`);
  }

  /** The delta of this call of consumeDelta in this step (each call returns the next recorded one, then zero). */
  delta(action: string): { x: number; y: number } {
    const key = `d:${action}`;
    const i = this.calls.get(key) ?? 0;
    this.calls.set(key, i + 1);
    const list = this.events.get(key) as [number, number][] | undefined;
    const d = list?.[i];
    return d ? { x: d[0], y: d[1] } : { x: 0, y: 0 };
  }
}
